import { useTranslation } from 'react-i18next';

import { useCurrentUserOrNull } from '@/shared/auth/hooks';
import { FileIcon } from '@/shared/components/files';
import { useFormatters } from '@/shared/i18n/format';
import type { SearchResult, SearchResultHighlights } from '@/shared/registry/types';

import { FolderGlyph } from './bits';

/** Global search hit of entity types `file` and `folder` (meta: storageKind, ownerId/ownerName, groupName, location, contentType, size, kind). */
export function DiskSearchResult({
  result,
  highlights,
}: {
  result: SearchResult;
  highlights?: SearchResultHighlights;
}) {
  const { t } = useTranslation('disk');
  const fmt = useFormatters();
  const me = useCurrentUserOrNull();
  const meta = result.meta ?? {};
  const folder = result.entityType === 'folder';
  const size = Number(meta.size);
  // Whose drive: "Мой диск", "Диск: Анна Смирнова", "Диск группы «Маркетинг»" (documents indexed before the names
  // were added say only the kind).
  const drive =
    meta.storageKind === 'company'
      ? t('storages.company')
      : meta.storageKind === 'group'
        ? meta.groupName
          ? t('storages.groupDrive', { name: meta.groupName })
          : t('storages.group')
        : meta.ownerId && meta.ownerId === me?.id
          ? t('storages.my')
          : meta.ownerName
            ? t('storages.userDrive', { name: meta.ownerName })
            : t('storages.personal');
  return (
    <>
      {folder ? (
        <FolderGlyph size="md" />
      ) : (
        <FileIcon name={result.title} contentType={meta.contentType ?? null} size="md" />
      )}
      <span className="min-w-0 flex-1" data-testid="disk-search-result">
        <span className="block truncate text-[0.9375rem] font-medium text-foreground">
          {highlights?.title ?? result.title}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <span>{drive}</span>
          {meta.location && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{meta.location}</span>
            </>
          )}
          {!folder && Number.isFinite(size) && size > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>{fmt.fileSize(size)}</span>
            </>
          )}
        </span>
        {result.snippet && result.snippet !== meta.location && (
          <span className="mt-1 line-clamp-2 block text-sm text-muted-foreground">
            {highlights?.snippet ?? result.snippet}
          </span>
        )}
        <span className="sr-only">{folder ? t('search.folderResult') : t('search.fileResult')}</span>
      </span>
    </>
  );
}
